import { useState, useEffect } from 'react';
import { Text, Box, Title, Stack, ActionIcon, Group } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import QualificationCard from './QualificationCard';
import { api } from '../apiClient';

// vendor 値を正規化（大文字小文字を吸収）
const normalizeVendor = (v) => v?.toLowerCase() ?? 'other';

const VENDOR_META = {
  aws:       { label: 'AWS',       defaultOrder: 0 },
  microsoft: { label: 'Microsoft', defaultOrder: 1 },
  google:    { label: 'Google',    defaultOrder: 2 },
};

// 資格名・正式名称からレベルを判定してソート順を返す
const getAWSLevel = (exam) => {
  const n = `${exam.name} ${exam.formal_name ?? ''}`.toLowerCase();
  if (n.includes('foundational') || n.includes('cloud practitioner')) return 0;
  if (n.includes('associate'))   return 1;
  if (n.includes('professional')) return 2;
  if (n.includes('specialty'))   return 3;
  return 4;
};

const getMicrosoftLevel = (exam) => {
  const n = `${exam.name} ${exam.formal_name ?? ''}`.toLowerCase();
  if (n.includes('fundamentals')) return 0;
  if (n.includes('associate'))    return 1;
  if (n.includes('expert'))       return 2;
  if (n.includes('specialty') || n.includes('specialist')) return 3;
  return 4;
};

const getGoogleLevel = (exam) => {
  const n = `${exam.name} ${exam.formal_name ?? ''}`.toLowerCase();
  if (n.includes('digital leader')) return 0;
  if (n.includes('associate'))      return 1;
  if (n.includes('professional'))   return 2;
  return 3;
};

const sortCerts = (certs, vendorKey) => {
  const getLevel = vendorKey === 'aws' ? getAWSLevel : vendorKey === 'google' ? getGoogleLevel : getMicrosoftLevel;
  return [...certs].sort((a, b) => {
    // Learning は最前列
    const aLearning = a.status === 'learning' ? 0 : 1;
    const bLearning = b.status === 'learning' ? 0 : 1;
    if (aLearning !== bLearning) return aLearning - bLearning;
    // Retired は最後尾
    const aRetired = a.status === 'retired' ? 1 : 0;
    const bRetired = b.status === 'retired' ? 1 : 0;
    if (aRetired !== bRetired) return aRetired - bRetired;
    return getLevel(a) - getLevel(b);
  });
};

function QualificationCardGrid({ user }) {
  const [examData, setExamData]     = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [loading, setLoading]       = useState(true);

  // ベンダー表示順序（localStorageで永続化）
  const [vendorOrder, setVendorOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('vendorOrder');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const fetchExamData = async () => {
      if (!user) {
        setExamData([]);
        setLoading(false);
        return;
      }

      try {
        const [certs, progress] = await Promise.all([
          api.get('/user-certification-statuses'),
          api.get('/user-certifications/progress'),
        ]);

        const formattedExamData = (certs ?? [])
          .map((c) => c ? { id: c.id, name: c.name, code: c.code, vendor: c.vendor, formal_name: c.formal_name, status: c.status } : null)
          .filter(Boolean);

        const pMap = {};
        for (const p of progress ?? []) {
          pMap[p.certification_id] = p;
        }

        setExamData(formattedExamData);
        setProgressMap(pMap);
      } catch (e) {
        console.error('データの取得に失敗しました:', e);
        setExamData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExamData();
  }, [user]);

  if (loading) return <Text>データを読み込み中...</Text>;
  if (examData.length === 0) return <Text c="dimmed" ta="center">表示できる資格がありません</Text>;

  // ベンダー別にグループ化（正規化キーで管理）
  const byVendor = examData.reduce((acc, exam) => {
    const key = normalizeVendor(exam.vendor);
    if (!acc[key]) acc[key] = [];
    acc[key].push(exam);
    return acc;
  }, {});

  const allVendors = Object.keys(byVendor);

  // 順序：保存済みがあればそれを使い、新しく出てきたベンダーを末尾に追加
  const resolvedOrder = (() => {
    const base = vendorOrder
      ? [
          ...vendorOrder.filter(v => allVendors.includes(v)),
          ...allVendors.filter(v => !vendorOrder.includes(v)),
        ]
      : [...allVendors].sort((a, b) => {
          const oa = VENDOR_META[a]?.defaultOrder ?? 99;
          const ob = VENDOR_META[b]?.defaultOrder ?? 99;
          return oa - ob;
        });
    return base;
  })();

  const moveVendor = (vendor, dir) => {
    const order = [...resolvedOrder];
    const idx = order.indexOf(vendor);
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= order.length) return;
    [order[idx], order[swapIdx]] = [order[swapIdx], order[idx]];
    setVendorOrder(order);
    localStorage.setItem('vendorOrder', JSON.stringify(order));
  };

  return (
    <Box px="md" py="xl" style={{ width: '100%' }}>
      <Stack gap="xl">
        {resolvedOrder.map((vendorKey, idx) => {
          const meta = VENDOR_META[vendorKey];
          const label = meta?.label ?? vendorKey;
          const certs = sortCerts(byVendor[vendorKey], vendorKey);

          return (
            <Box key={vendorKey}>
              {/* セクションヘッダー */}
              <Group gap="xs" mb="md" align="center">
                <Title order={3}>{label}</Title>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  disabled={idx === 0}
                  onClick={() => moveVendor(vendorKey, 'up')}
                  aria-label="上へ"
                >
                  <IconChevronUp size={14} />
                </ActionIcon>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  disabled={idx === resolvedOrder.length - 1}
                  onClick={() => moveVendor(vendorKey, 'down')}
                  aria-label="下へ"
                >
                  <IconChevronDown size={14} />
                </ActionIcon>
              </Group>

              {/* 横スクロール（2段） */}
              <Box
                style={{
                  display: 'grid',
                  gridTemplateRows: 'repeat(2, auto)',
                  gridAutoFlow: 'column',
                  gridAutoColumns: '280px',
                  overflowX: 'auto',
                  gap: '16px',
                  padding: '4px 4px 8px 4px',
                  width: '100%',
                  minWidth: 0,
                }}
              >
                {certs.map((exam) => (
                  <QualificationCard
                    key={exam.id}
                    exam={exam}
                    progress={progressMap[exam.id] ?? null}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default QualificationCardGrid;
